import { type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Zap, Flame, Sparkles, Wand2, Sword, Swords, XCircle } from 'lucide-react';
import { SectionWrapper } from './CharDetailsSections';
import { spellTooltipText } from './actions-section-utils';
import { UNLIMITED_USES } from './card-uses';

// =============================================================================
// Types
// =============================================================================

export interface ActionSpell {
  name: string;
  emoji?: string;
  summary?: string;
  level: number;
  isCantrip: boolean;
  slotsRemaining?: number;
  maxSlots?: number;
  /** Species/feat innate spell with no slot pool — runtime free-cast pipeline
   *  gates usage. See `collectSpellOptions`. */
  freeCastable?: boolean;
  /** Remaining innate free casts until long rest (1/LR for Elf/Tiefling/MI, PB/LR
   *  for Forest Gnome Speak with Animals). `undefined` = quota unknown (legacy
   *  caller omitted the FreeCastContext). When 0, the button is rendered disabled. */
  freeCastRemaining?: number;
  /** Maximum innate free casts per long rest. */
  freeCastMax?: number;
}

/**
 * Wrap a spell pill in a tooltip that ALWAYS renders (#246) — see
 * `spellTooltipText` for the content/fallback rule.
 */
function renderSpellTooltip(spell: ActionSpell, btn: ReactNode, override?: string): ReactNode {
  const content = spellTooltipText(spell, override);
  return (
    <TooltipProvider key={spell.name}>
      <Tooltip>
        <TooltipTrigger asChild>{btn}</TooltipTrigger>
        <TooltipContent className="max-w-64">{content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface ActionItem {
  name: string;
  emoji?: string;
  charges?: number;
  isDepleted?: boolean;
  itemId?: string;
  effectType?: 'cast_spell' | 'cast_spell_charges' | 'special_abilities' | 'targeted' | 'self';
  spells?: Array<{ spellName: string; charges: number; target_audience?: string }>;
  abilities?: Array<{ name: string; trigger: string; charges?: number }>;
}

export interface ActionWeapon {
  name: string;
  emoji?: string;
  bonus?: string;
  damage?: string;
}

export interface ActionAbility {
  name: string;
  emoji?: string;
  description?: string;
  remainingUses: number;
  maxUses: number;
  recharge: string;
  targetType?: 'self_only' | 'targeted';
}

export interface ActiveState {
  type: 'concentration';
  label: string;
}

interface ActionsSectionProps {
  expanded: boolean;
  onToggle: () => void;
  showSeparator: boolean;
  spells: ActionSpell[];
  weapons: ActionWeapon[];
  items: ActionItem[];
  abilities: ActionAbility[];
  activeStates: ActiveState[];
  onSpellClick: (spellName: string, spellLevel: number, freeCast?: boolean) => void;
  onWeaponAttackClick: (weaponName: string) => void;
  onAbilityClick: (abilityName: string, targetType?: 'self_only' | 'targeted') => void;
  onItemClick: (item: ActionItem) => void;
  onEndState: (state: ActiveState) => void;
  disabled?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function ActionsSection({
  expanded,
  onToggle,
  showSeparator,
  spells,
  weapons,
  items,
  abilities,
  activeStates,
  onSpellClick,
  onWeaponAttackClick,
  onAbilityClick,
  onItemClick,
  onEndState,
  disabled,
}: ActionsSectionProps) {
  const cantrips = spells.filter((s) => s.isCantrip && !s.freeCastable);
  const innateSpells = spells.filter((s) => s.freeCastable);
  const leveledSpells = spells.filter((s) => !s.isCantrip && !s.freeCastable);

  // Group leveled spells by level
  const spellsByLevel = new Map<number, ActionSpell[]>();
  for (const spell of leveledSpells) {
    const group = spellsByLevel.get(spell.level) ?? [];
    group.push(spell);
    spellsByLevel.set(spell.level, group);
  }
  const sortedLevels = [...spellsByLevel.keys()].sort((a, b) => a - b);

  const usableSpellCount = spells.filter((s) => {
    if (s.isCantrip) return true;
    if (s.freeCastable) {
      // Innate spells count as usable unless quota is known to be exhausted.
      return s.freeCastRemaining == null || s.freeCastRemaining > 0;
    }
    return (s.slotsRemaining ?? 0) > 0;
  }).length;
  const totalActions =
    usableSpellCount +
    weapons.length +
    items.filter((item) => !item.isDepleted).length +
    abilities.filter((a) => a.remainingUses > 0).length +
    activeStates.length;

  return (
    <SectionWrapper
      title={
        <>
          <Zap className="h-4 w-4 text-yellow-500" />
          Actions
        </>
      }
      badge={
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0 uppercase tracking-wide bg-muted/40 backdrop-blur-sm border-foreground/20"
        >
          {totalActions}
        </Badge>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      <div
        className={`space-y-3 ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        aria-disabled={disabled}
        inert={disabled ? true : undefined}
      >
        {/* Active States */}
        {activeStates.length > 0 && (
          <div>
            <div className="space-y-1">
              {activeStates.map((state) => (
                <button
                  key={state.type}
                  onClick={() => onEndState(state)}
                  className="flex items-center justify-between w-full p-1.5 rounded bg-amber-500/15 hover:bg-amber-500/25 dark:bg-amber-500/15 dark:hover:bg-amber-500/25 backdrop-blur-sm transition-colors text-left"
                >
                  <span className="text-xs font-medium flex items-center gap-1.5 text-foreground dark:text-amber-50">
                    <XCircle className="h-3.5 w-3.5 text-foreground/60 dark:text-amber-200" />
                    {state.label}
                  </span>
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 uppercase tracking-wide text-foreground dark:text-amber-100 border-foreground/30 dark:border-amber-200/40"
                  >
                    End
                  </Badge>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Weapon Attacks */}
        {weapons.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
              <Sword className="h-3 w-3" />
              Attacks
            </h4>
            <div className="space-y-1">
              {weapons.map((weapon) => (
                <button
                  key={weapon.name}
                  onClick={() => onWeaponAttackClick(weapon.name)}
                  className="flex w-full items-center justify-between rounded bg-rose-500/15 hover:bg-rose-500/25 dark:bg-rose-500/15 dark:hover:bg-rose-500/25 backdrop-blur-sm px-2 py-1.5 text-left text-xs transition-colors"
                >
                  <span className="font-medium text-foreground dark:text-rose-50">
                    {weapon.emoji && <span className="mr-0.5">{weapon.emoji}</span>}
                    {weapon.name}
                  </span>
                  {(weapon.bonus || weapon.damage) && (
                    <span className="text-muted-foreground dark:text-rose-100/80">
                      {[weapon.bonus, weapon.damage].filter(Boolean).join(' ')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Cantrips */}
        {cantrips.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
              <Flame className="h-3 w-3" />
              Cantrips
            </h4>
            <div className="flex flex-wrap gap-1">
              {cantrips.map((spell) => {
                const btn = (
                  <button
                    key={spell.name}
                    onClick={() => onSpellClick(spell.name, 0)}
                    className="px-2 py-0.5 text-xs rounded-full bg-violet-500/15 text-foreground hover:bg-violet-500/25 dark:bg-violet-500/15 dark:text-violet-50 dark:hover:bg-violet-500/25 backdrop-blur-sm transition-colors cursor-pointer"
                  >
                    {spell.emoji && <span className="mr-0.5">{spell.emoji}</span>}
                    {spell.name}
                  </button>
                );
                return renderSpellTooltip(spell, btn);
              })}
            </div>
          </div>
        )}

        {/* Innate Spells (species lineage / origin feat — runtime free-cast quota) */}
        {innateSpells.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Innate
            </h4>
            <div className="flex flex-wrap gap-1">
              {innateSpells.map((spell) => {
                // Quota gating: when we know the remaining uses and it's 0, the
                // spell is depleted until the next long rest. Render as disabled
                // so the player sees the constraint without trial-and-error.
                // When `freeCastRemaining` is undefined (legacy caller without
                // FreeCastContext), fall back to "always clickable" pre-quota
                // behaviour — runtime still rejects the cast via a warning.
                const quotaKnown = spell.freeCastRemaining != null && spell.freeCastMax != null;
                const isExhausted = quotaKnown && spell.freeCastRemaining === 0;
                const quotaLabel = quotaKnown
                  ? ` ${spell.freeCastRemaining}/${spell.freeCastMax}`
                  : '';
                const btn = (
                  <button
                    key={spell.name}
                    onClick={() => !isExhausted && onSpellClick(spell.name, spell.level, true)}
                    disabled={isExhausted}
                    className={
                      isExhausted
                        ? 'px-2 py-0.5 text-xs rounded-full bg-muted/40 text-muted-foreground backdrop-blur-sm cursor-not-allowed opacity-70'
                        : 'px-2 py-0.5 text-xs rounded-full bg-amber-500/15 text-foreground hover:bg-amber-500/25 dark:bg-amber-500/15 dark:text-amber-50 dark:hover:bg-amber-500/25 backdrop-blur-sm transition-colors cursor-pointer'
                    }
                    title={
                      isExhausted
                        ? `Innate spell — free cast used this long rest (${spell.freeCastRemaining}/${spell.freeCastMax})`
                        : `Innate spell — free cast per long rest${quotaLabel ? ` (${spell.freeCastRemaining}/${spell.freeCastMax})` : ''}`
                    }
                  >
                    {spell.emoji && <span className="mr-0.5">{spell.emoji}</span>}
                    {spell.name}
                    {quotaLabel && (
                      <span className="ml-1 text-muted-foreground dark:text-amber-100/70">
                        {quotaLabel}
                      </span>
                    )}
                  </button>
                );
                return renderSpellTooltip(spell, btn);
              })}
            </div>
          </div>
        )}

        {/* Leveled Spells */}
        {sortedLevels.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Spells
            </h4>
            <div className="space-y-1">
              {sortedLevels.map((level) => {
                const group = spellsByLevel.get(level)!;
                const slots = group[0];
                const depleted = (slots.slotsRemaining ?? 0) === 0;
                return (
                  <div key={level} className="space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">LVL {level}</span>
                      <Badge
                        variant="outline"
                        className={
                          depleted
                            ? 'text-[10px] h-4 px-1 py-0 uppercase tracking-wide bg-muted/40 backdrop-blur-sm border-foreground/20'
                            : 'text-[10px] h-4 px-1 py-0 uppercase tracking-wide bg-blue-500/15 border-blue-500/40 backdrop-blur-sm text-foreground dark:text-blue-100'
                        }
                      >
                        {slots.slotsRemaining}/{slots.maxSlots}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {group.map((spell) => {
                        const btn = (
                          <button
                            key={spell.name}
                            onClick={() => !depleted && onSpellClick(spell.name, spell.level)}
                            className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                              depleted
                                ? 'bg-muted/40 text-muted-foreground backdrop-blur-sm opacity-70 cursor-not-allowed'
                                : 'bg-blue-500/15 text-foreground hover:bg-blue-500/25 dark:bg-blue-500/15 dark:text-blue-50 dark:hover:bg-blue-500/25 backdrop-blur-sm cursor-pointer'
                            }`}
                            disabled={depleted}
                          >
                            {spell.emoji && <span className="mr-0.5">{spell.emoji}</span>}
                            {spell.name}
                          </button>
                        );
                        return renderSpellTooltip(
                          spell,
                          btn,
                          depleted ? 'Out of slots' : undefined
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Magic Items */}
        {items.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
              <Wand2 className="h-3 w-3" />
              Magic Items
            </h4>
            <div className="flex flex-wrap gap-1">
              {items.map((item) => (
                <button
                  key={item.itemId ?? item.name}
                  onClick={() => !item.isDepleted && onItemClick(item)}
                  className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                    item.isDepleted
                      ? 'bg-muted/40 text-muted-foreground backdrop-blur-sm opacity-70 cursor-not-allowed'
                      : 'bg-emerald-500/15 text-foreground hover:bg-emerald-500/25 dark:bg-emerald-500/15 dark:text-emerald-50 dark:hover:bg-emerald-500/25 backdrop-blur-sm cursor-pointer'
                  }`}
                  disabled={item.isDepleted}
                >
                  {item.emoji && <span className="mr-0.5">{item.emoji}</span>}
                  {item.name}
                  {item.charges != null && (
                    <span className="ml-1 text-muted-foreground dark:text-emerald-100/70">
                      ({item.charges})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Class Abilities */}
        {abilities.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
              <Swords className="h-3 w-3" />
              Abilities
            </h4>
            <div className="flex flex-wrap gap-1">
              {abilities.map((ability) => {
                const depleted = ability.remainingUses <= 0;
                const btn = (
                  <button
                    key={ability.name}
                    onClick={() => !depleted && onAbilityClick(ability.name, ability.targetType)}
                    className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                      depleted
                        ? 'bg-muted/40 text-muted-foreground backdrop-blur-sm opacity-70 cursor-not-allowed'
                        : 'bg-orange-500/15 text-foreground hover:bg-orange-500/25 dark:bg-orange-500/15 dark:text-orange-50 dark:hover:bg-orange-500/25 backdrop-blur-sm cursor-pointer'
                    }`}
                    disabled={depleted}
                  >
                    {ability.emoji && <span className="mr-0.5">{ability.emoji}</span>}
                    {ability.name}
                    <span className="ml-1 text-muted-foreground dark:text-orange-100/70">
                      {/* An unlimited pool (level-20 Rage, Wild Shape) carries a
                          sentinel max — show it as such rather than printing
                          the number. */}
                      {ability.maxUses >= UNLIMITED_USES
                        ? '∞'
                        : `${ability.remainingUses}/${ability.maxUses}`}
                    </span>
                  </button>
                );
                if (!ability.description) return btn;
                return (
                  <TooltipProvider key={ability.name}>
                    <Tooltip>
                      <TooltipTrigger asChild>{btn}</TooltipTrigger>
                      <TooltipContent className="max-w-64">{ability.description}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
}
